
import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import databaseService from '../services/DatabaseService';
import apiService from '../services/ApiService';
import syncService from '../services/SyncService';

/**
 * Database Context for JobEng App
 * Provides centralized data management and real-time updates for all screens
 */

// Initial state
const initialState = {
  // Connection status
  isInitialized: false,
  isOnline: true,
  isSyncing: false,
  
  // User data
  currentUser: null,
  userProfile: null,
  
  // Dashboard data
  dashboardStats: {
    activeTasks: 0,
    completedTasks: 0,
    activeProjects: 0,
    pendingApprovals: 0,
    todayHours: 0,
    weekHours: 0,
    monthHours: 0,
  },
  
  // Core data
  projects: [],
  tasks: [],
  timeEntries: [],
  notifications: [],
  leaveRequests: [],
  expenses: [],
  companies: [],
  roles: [],
  
  // UI state
  loading: {
    projects: false,
    tasks: false,
    timeEntries: false,
    notifications: false,
    leaveRequests: false,
    expenses: false,
    dashboard: false,
  },
  
  errors: {},
  lastUpdated: {},
  
  // Sync status
  syncStatus: {
    lastSync: null,
    pendingUploads: 0,
    conflicts: 0,
  },
};

// Action types
const ActionTypes = {
  // Initialization
  SET_INITIALIZED: 'SET_INITIALIZED',
  SET_ONLINE_STATUS: 'SET_ONLINE_STATUS',
  SET_SYNC_STATUS: 'SET_SYNC_STATUS',
  
  // User actions
  SET_CURRENT_USER: 'SET_CURRENT_USER',
  SET_USER_PROFILE: 'SET_USER_PROFILE',
  
  // Dashboard actions
  SET_DASHBOARD_STATS: 'SET_DASHBOARD_STATS',
  
  // Data actions
  SET_PROJECTS: 'SET_PROJECTS',
  ADD_PROJECT: 'ADD_PROJECT',
  UPDATE_PROJECT: 'UPDATE_PROJECT',
  DELETE_PROJECT: 'DELETE_PROJECT',
  
  SET_TASKS: 'SET_TASKS',
  ADD_TASK: 'ADD_TASK',
  UPDATE_TASK: 'UPDATE_TASK',
  DELETE_TASK: 'DELETE_TASK',
  
  SET_TIME_ENTRIES: 'SET_TIME_ENTRIES',
  ADD_TIME_ENTRY: 'ADD_TIME_ENTRY',
  UPDATE_TIME_ENTRY: 'UPDATE_TIME_ENTRY',
  DELETE_TIME_ENTRY: 'DELETE_TIME_ENTRY',
  
  SET_NOTIFICATIONS: 'SET_NOTIFICATIONS',
  ADD_NOTIFICATION: 'ADD_NOTIFICATION',
  UPDATE_NOTIFICATION: 'UPDATE_NOTIFICATION',
  MARK_NOTIFICATION_READ: 'MARK_NOTIFICATION_READ',
  
  SET_LEAVE_REQUESTS: 'SET_LEAVE_REQUESTS',
  ADD_LEAVE_REQUEST: 'ADD_LEAVE_REQUEST',
  UPDATE_LEAVE_REQUEST: 'UPDATE_LEAVE_REQUEST',
  
  SET_EXPENSES: 'SET_EXPENSES',
  ADD_EXPENSE: 'ADD_EXPENSE',
  UPDATE_EXPENSE: 'UPDATE_EXPENSE',
  DELETE_EXPENSE: 'DELETE_EXPENSE',
  
  SET_COMPANIES: 'SET_COMPANIES',
  SET_ROLES: 'SET_ROLES',
  
  // UI actions
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_LAST_UPDATED: 'SET_LAST_UPDATED',
};

// Reducer
function databaseReducer(state, action) {
  switch (action.type) {
    case ActionTypes.SET_INITIALIZED:
      return { ...state, isInitialized: action.payload };
      
    case ActionTypes.SET_ONLINE_STATUS:
      return { ...state, isOnline: action.payload };
      
    case ActionTypes.SET_SYNC_STATUS:
      return { ...state, isSyncing: action.payload.isSyncing, syncStatus: action.payload.status };
      
    case ActionTypes.SET_CURRENT_USER:
      return { ...state, currentUser: action.payload };
      
    case ActionTypes.SET_USER_PROFILE:
      return { ...state, userProfile: action.payload };
      
    case ActionTypes.SET_DASHBOARD_STATS:
      return { ...state, dashboardStats: { ...state.dashboardStats, ...action.payload } };
      
    case ActionTypes.SET_PROJECTS:
      return { 
        ...state, 
        projects: action.payload,
        lastUpdated: { ...state.lastUpdated, projects: Date.now() }
      };
      
    case ActionTypes.ADD_PROJECT:
      return { 
        ...state, 
        projects: [action.payload, ...state.projects],
        lastUpdated: { ...state.lastUpdated, projects: Date.now() }
      };
      
    case ActionTypes.UPDATE_PROJECT:
      return {
        ...state,
        projects: state.projects.map(project =>
          project.id === action.payload.id ? { ...project, ...action.payload } : project
        ),
        lastUpdated: { ...state.lastUpdated, projects: Date.now() }
      };
      
    case ActionTypes.DELETE_PROJECT:
      return {
        ...state,
        projects: state.projects.filter(project => project.id !== action.payload),
        lastUpdated: { ...state.lastUpdated, projects: Date.now() }
      };
      
    case ActionTypes.SET_TASKS:
      return { 
        ...state, 
        tasks: action.payload,
        lastUpdated: { ...state.lastUpdated, tasks: Date.now() }
      };
      
    case ActionTypes.ADD_TASK:
      return { 
        ...state, 
        tasks: [action.payload, ...state.tasks],
        lastUpdated: { ...state.lastUpdated, tasks: Date.now() }
      };
      
    case ActionTypes.UPDATE_TASK:
      return {
        ...state,
        tasks: state.tasks.map(task =>
          task.id === action.payload.id ? { ...task, ...action.payload } : task
        ),
        lastUpdated: { ...state.lastUpdated, tasks: Date.now() }
      };
      
    case ActionTypes.DELETE_TASK:
      return {
        ...state,
        tasks: state.tasks.filter(task => task.id !== action.payload),
        lastUpdated: { ...state.lastUpdated, tasks: Date.now() }
      };
      
    case ActionTypes.SET_TIME_ENTRIES:
      return { 
        ...state, 
        timeEntries: action.payload,
        lastUpdated: { ...state.lastUpdated, timeEntries: Date.now() }
      };
      
    case ActionTypes.ADD_TIME_ENTRY:
      return { 
        ...state, 
        timeEntries: [action.payload, ...state.timeEntries],
        lastUpdated: { ...state.lastUpdated, timeEntries: Date.now() }
      };
      
    case ActionTypes.UPDATE_TIME_ENTRY:
      return {
        ...state,
        timeEntries: state.timeEntries.map(entry =>
          entry.id === action.payload.id ? { ...entry, ...action.payload } : entry
        ),
        lastUpdated: { ...state.lastUpdated, timeEntries: Date.now() }
      };
      
    case ActionTypes.DELETE_TIME_ENTRY:
      return {
        ...state,
        timeEntries: state.timeEntries.filter(entry => entry.id !== action.payload),
        lastUpdated: { ...state.lastUpdated, timeEntries: Date.now() }
      };
      
    case ActionTypes.SET_NOTIFICATIONS:
      return { 
        ...state, 
        notifications: action.payload,
        lastUpdated: { ...state.lastUpdated, notifications: Date.now() }
      };
      
    case ActionTypes.ADD_NOTIFICATION:
      return { 
        ...state, 
        notifications: [action.payload, ...state.notifications],
        lastUpdated: { ...state.lastUpdated, notifications: Date.now() }
      };
      
    case ActionTypes.UPDATE_NOTIFICATION:
      return {
        ...state,
        notifications: state.notifications.map(notification =>
          notification.id === action.payload.id ? { ...notification, ...action.payload } : notification
        ),
        lastUpdated: { ...state.lastUpdated, notifications: Date.now() }
      };
      
    case ActionTypes.MARK_NOTIFICATION_READ:
      return {
        ...state,
        notifications: state.notifications.map(notification =>
          notification.id === action.payload ? { ...notification, read: 1 } : notification
        ),
        lastUpdated: { ...state.lastUpdated, notifications: Date.now() }
      };
      
    case ActionTypes.SET_LEAVE_REQUESTS:
      return { 
        ...state, 
        leaveRequests: action.payload,
        lastUpdated: { ...state.lastUpdated, leaveRequests: Date.now() }
      };
      
    case ActionTypes.ADD_LEAVE_REQUEST:
      return { 
        ...state, 
        leaveRequests: [action.payload, ...state.leaveRequests],
        lastUpdated: { ...state.lastUpdated, leaveRequests: Date.now() }
      };
      
    case ActionTypes.UPDATE_LEAVE_REQUEST:
      return {
        ...state,
        leaveRequests: state.leaveRequests.map(request =>
          request.id === action.payload.id ? { ...request, ...action.payload } : request
        ),
        lastUpdated: { ...state.lastUpdated, leaveRequests: Date.now() }
      };
      
    case ActionTypes.SET_EXPENSES:
      return { 
        ...state, 
        expenses: action.payload,
        lastUpdated: { ...state.lastUpdated, expenses: Date.now() }
      };
      
    case ActionTypes.ADD_EXPENSE:
      return { 
        ...state, 
        expenses: [action.payload, ...state.expenses],
        lastUpdated: { ...state.lastUpdated, expenses: Date.now() }
      };
      
    case ActionTypes.UPDATE_EXPENSE:
      return {
        ...state,
        expenses: state.expenses.map(expense =>
          expense.id === action.payload.id ? { ...expense, ...action.payload } : expense
        ),
        lastUpdated: { ...state.lastUpdated, expenses: Date.now() }
      };
      
    case ActionTypes.DELETE_EXPENSE:
      return {
        ...state,
        expenses: state.expenses.filter(expense => expense.id !== action.payload),
        lastUpdated: { ...state.lastUpdated, expenses: Date.now() }
      };
      
    case ActionTypes.SET_COMPANIES:
      return { ...state, companies: action.payload };
      
    case ActionTypes.SET_ROLES:
      return { ...state, roles: action.payload };
      
    case ActionTypes.SET_LOADING:
      return {
        ...state,
        loading: { ...state.loading, [action.payload.key]: action.payload.value }
      };
      
    case ActionTypes.SET_ERROR:
      return {
        ...state,
        errors: { ...state.errors, [action.payload.key]: action.payload.error }
      };
      
    case ActionTypes.CLEAR_ERROR:
      const newErrors = { ...state.errors };
      delete newErrors[action.payload];
      return { ...state, errors: newErrors };
      
    case ActionTypes.SET_LAST_UPDATED:
      return {
        ...state,
        lastUpdated: { ...state.lastUpdated, [action.payload.key]: action.payload.timestamp }
      };
      
    default:
      return state;
  }
}

// Create context
const DatabaseContext = createContext();

// Provider component
export function DatabaseProvider({ children }) {
  const [state, dispatch] = useReducer(databaseReducer, initialState);

  // Initialize database services
  useEffect(() => {
    initializeServices();
  }, []);

  // Set up sync listeners
  useEffect(() => {
    const handleSyncEvent = (event, data) => {
      switch (event) {
        case 'sync_started':
          dispatch({
            type: ActionTypes.SET_SYNC_STATUS,
            payload: { isSyncing: true, status: state.syncStatus }
          });
          break;
          
        case 'sync_completed':
          dispatch({
            type: ActionTypes.SET_SYNC_STATUS,
            payload: {
              isSyncing: false,
              status: {
                ...state.syncStatus,
                lastSync: new Date(),
                pendingUploads: 0,
              }
            }
          });
          // Refresh data after sync
          refreshAllData();
          break;
          
        case 'sync_failed':
          dispatch({
            type: ActionTypes.SET_SYNC_STATUS,
            payload: { isSyncing: false, status: state.syncStatus }
          });
          break;
      }
    };

    syncService.addSyncListener(handleSyncEvent);
    
    return () => {
      syncService.removeSyncListener(handleSyncEvent);
    };
  }, [state.syncStatus]);

  // Initialize all services
  const initializeServices = async () => {
    try {
      console.log('🔄 Initializing database services...');
      
      // Initialize database
      const dbResult = await databaseService.initialize();
      if (!dbResult.success) {
        throw new Error(`Database initialization failed: ${dbResult.error}`);
      }
      
      // Initialize API service
      const apiResult = await apiService.initialize();
      if (!apiResult.success) {
        console.warn('API service initialization failed:', apiResult.error);
      }
      
      // Initialize sync service
      const syncResult = await syncService.initialize();
      if (!syncResult.success) {
        console.warn('Sync service initialization failed:', syncResult.error);
      }
      
      dispatch({ type: ActionTypes.SET_INITIALIZED, payload: true });
      console.log('✅ Database services initialized successfully');
      
    } catch (error) {
      console.error('❌ Failed to initialize database services:', error);
      dispatch({ type: ActionTypes.SET_ERROR, payload: { key: 'initialization', error: error?.message || 'Unknown error' } });
    }
  };

  // Utility functions
  const setLoading = useCallback((key, value) => {
    dispatch({ type: ActionTypes.SET_LOADING, payload: { key, value } });
  }, []);

  const setError = useCallback((key, error) => {
    dispatch({ type: ActionTypes.SET_ERROR, payload: { key, error } });
  }, []);

  const clearError = useCallback((key) => {
    dispatch({ type: ActionTypes.CLEAR_ERROR, payload: key });
  }, []);

  // User management
  const setCurrentUser = useCallback((user) => {
    dispatch({ type: ActionTypes.SET_CURRENT_USER, payload: user });
  }, []);

  const updateUserProfile = useCallback(async (profileData) => {
    try {
      setLoading('profile', true);
      clearError('profile');
      
      // Update in database
      const dbResult = await databaseService.updateUser(state.currentUser.id, profileData);
      if (!dbResult.success) {
        throw new Error(dbResult.error);
      }
      
      // Update in API if online
      if (state.isOnline) {
        const apiResult = await apiService.updateProfile(profileData);
        if (!apiResult.success) {
          console.warn('Failed to update profile on server:', apiResult.error);
        }
      }
      
      const updatedUser = { ...state.currentUser, ...profileData };
      dispatch({ type: ActionTypes.SET_CURRENT_USER, payload: updatedUser });
      dispatch({ type: ActionTypes.SET_USER_PROFILE, payload: updatedUser });
      
      return { success: true, data: updatedUser };
      
    } catch (error) {
      setError('profile', error?.message || 'Unknown error');
      return { success: false, error: error?.message || 'Unknown error' };
    } finally {
      setLoading('profile', false);
    }
  }, [state.currentUser, state.isOnline]);

  // Dashboard data
  const loadDashboardStats = useCallback(async (userId) => {
    try {
      setLoading('dashboard', true);
      clearError('dashboard');
      
      const result = await databaseService.getDashboardStats(userId);
      if (result.success) {
        dispatch({ type: ActionTypes.SET_DASHBOARD_STATS, payload: result.data });
      } else {
        throw new Error(result.error);
      }
      
      return result;
      
    } catch (error) {
      setError('dashboard', error?.message || 'Unknown error');
      return { success: false, error: error?.message || 'Unknown error' };
    } finally {
      setLoading('dashboard', false);
    }
  }, []);

  // Project management
  const loadProjects = useCallback(async (userId) => {
    try {
      setLoading('projects', true);
      clearError('projects');
      
      const result = await databaseService.getProjectsByUser(userId);
      if (result.success) {
        dispatch({ type: ActionTypes.SET_PROJECTS, payload: result.data });
      } else {
        throw new Error(result.error);
      }
      
      return result;
      
    } catch (error) {
      setError('projects', error?.message || 'Unknown error');
      return { success: false, error: error?.message || 'Unknown error' };
    } finally {
      setLoading('projects', false);
    }
  }, []);

  const createProject = useCallback(async (projectData) => {
    try {
      const result = await databaseService.createProject({
        ...projectData,
        manager_id: state.currentUser?.id,
      });
      
      if (result.success) {
        dispatch({ type: ActionTypes.ADD_PROJECT, payload: result.data });
      }
      
      return result;
      
    } catch (error) {
      return { success: false, error: error?.message || 'Unknown error' };
    }
  }, [state.currentUser]);

  const updateProject = useCallback(async (projectId, projectData) => {
    try {
      const result = await databaseService.update('projects', projectId, projectData);
      
      if (result.success) {
        dispatch({ type: ActionTypes.UPDATE_PROJECT, payload: result.data });
      }
      
      return result;
      
    } catch (error) {
      return { success: false, error: error?.message || 'Unknown error' };
    }
  }, []);

  const deleteProject = useCallback(async (projectId) => {
    try {
      const result = await databaseService.delete('projects', projectId);
      
      if (result.success) {
        dispatch({ type: ActionTypes.DELETE_PROJECT, payload: projectId });
      }
      
      return result;
      
    } catch (error) {
      return { success: false, error: error?.message || 'Unknown error' };
    }
  }, []);

  // Task management
  const loadTasks = useCallback(async (userId) => {
    try {
      setLoading('tasks', true);
      clearError('tasks');
      
      const result = await databaseService.getTasksByUser(userId);
      if (result.success) {
        dispatch({ type: ActionTypes.SET_TASKS, payload: result.data });
      } else {
        throw new Error(result.error);
      }
      
      return result;
      
    } catch (error) {
      setError('tasks', error?.message || 'Unknown error');
      return { success: false, error: error?.message || 'Unknown error' };
    } finally {
      setLoading('tasks', false);
    }
  }, []);

  const createTask = useCallback(async (taskData) => {
    try {
      const result = await databaseService.createTask({
        ...taskData,
        assigned_by: state.currentUser?.id,
      });
      
      if (result.success) {
        dispatch({ type: ActionTypes.ADD_TASK, payload: result.data });
        
        // Create notification for assigned user
        if (taskData.assigned_to && taskData.assigned_to !== state.currentUser?.id) {
          await createNotification({
            user_id: taskData.assigned_to,
            title: 'New Task Assigned',
            message: `You have been assigned to: ${taskData.title}`,
            type: 'task',
            data: JSON.stringify({ task_id: result.data.id }),
          });
        }
      }
      
      return result;
      
    } catch (error) {
      return { success: false, error: error?.message || 'Unknown error' };
    }
  }, [state.currentUser]);

  const updateTask = useCallback(async (taskId, taskData) => {
    try {
      const result = await databaseService.update('tasks', taskId, taskData);
      
      if (result.success) {
        dispatch({ type: ActionTypes.UPDATE_TASK, payload: result.data });
      }
      
      return result;
      
    } catch (error) {
      return { success: false, error: error?.message || 'Unknown error' };
    }
  }, []);

  const updateTaskProgress = useCallback(async (taskId, progress) => {
    try {
      const result = await databaseService.updateTaskProgress(taskId, progress);
      
      if (result.success) {
        dispatch({ type: ActionTypes.UPDATE_TASK, payload: { id: taskId, progress } });
      }
      
      return result;
      
    } catch (error) {
      return { success: false, error: error?.message || 'Unknown error' };
    }
  }, []);

  const deleteTask = useCallback(async (taskId) => {
    try {
      const result = await databaseService.delete('tasks', taskId);
      
      if (result.success) {
        dispatch({ type: ActionTypes.DELETE_TASK, payload: taskId });
      }
      
      return result;
      
    } catch (error) {
      return { success: false, error: error?.message || 'Unknown error' };
    }
  }, []);

  // Time tracking
  const loadTimeEntries = useCallback(async (userId, date = null) => {
    try {
      setLoading('timeEntries', true);
      clearError('timeEntries');
      
      const result = await databaseService.getTimeEntriesByUser(userId, date);
      if (result.success) {
        dispatch({ type: ActionTypes.SET_TIME_ENTRIES, payload: result.data });
      } else {
        throw new Error(result.error);
      }
      
      return result;
      
    } catch (error) {
      setError('timeEntries', error?.message || 'Unknown error');
      return { success: false, error: error?.message || 'Unknown error' };
    } finally {
      setLoading('timeEntries', false);
    }
  }, []);

  const createTimeEntry = useCallback(async (timeData) => {
    try {
      const result = await databaseService.createTimeEntry({
        ...timeData,
        user_id: state.currentUser?.id,
      });
      
      if (result.success) {
        dispatch({ type: ActionTypes.ADD_TIME_ENTRY, payload: result.data });
      }
      
      return result;
      
    } catch (error) {
      return { success: false, error: error?.message || 'Unknown error' };
    }
  }, [state.currentUser]);

  const updateTimeEntry = useCallback(async (entryId, timeData) => {
    try {
      const result = await databaseService.update('time_entries', entryId, timeData);
      
      if (result.success) {
        dispatch({ type: ActionTypes.UPDATE_TIME_ENTRY, payload: result.data });
      }
      
      return result;
      
    } catch (error) {
      return { success: false, error: error?.message || 'Unknown error' };
    }
  }, []);

  const deleteTimeEntry = useCallback(async (entryId) => {
    try {
      const result = await databaseService.delete('time_entries', entryId);
      
      if (result.success) {
        dispatch({ type: ActionTypes.DELETE_TIME_ENTRY, payload: entryId });
      }
      
      return result;
      
    } catch (error) {
      return { success: false, error: error?.message || 'Unknown error' };
    }
  }, []);

  // Notifications
  const loadNotifications = useCallback(async (userId) => {
    try {
      setLoading('notifications', true);
      clearError('notifications');
      
      const result = await databaseService.getNotificationsByUser(userId);
      if (result.success) {
        dispatch({ type: ActionTypes.SET_NOTIFICATIONS, payload: result.data });
      } else {
        throw new Error(result.error);
      }
      
      return result;
      
    } catch (error) {
      setError('notifications', error?.message || 'Unknown error');
      return { success: false, error: error?.message || 'Unknown error' };
    } finally {
      setLoading('notifications', false);
    }
  }, []);

  const createNotification = useCallback(async (notificationData) => {
    try {
      const result = await databaseService.createNotification(notificationData);
      
      if (result.success) {
        dispatch({ type: ActionTypes.ADD_NOTIFICATION, payload: result.data });
      }
      
      return result;
      
    } catch (error) {
      return { success: false, error: error?.message || 'Unknown error' };
    }
  }, []);

  const markNotificationAsRead = useCallback(async (notificationId) => {
    try {
      const result = await databaseService.markNotificationAsRead(notificationId);
      
      if (result.success) {
        dispatch({ type: ActionTypes.MARK_NOTIFICATION_READ, payload: notificationId });
      }
      
      return result;
      
    } catch (error) {
      return { success: false, error: error?.message || 'Unknown error' };
    }
  }, []);

  // Leave requests
  const loadLeaveRequests = useCallback(async (userId) => {
    try {
      setLoading('leaveRequests', true);
      clearError('leaveRequests');
      
      const result = await databaseService.findAll('leave_requests', {
        where: { user_id: userId },
        orderBy: 'created_at',
        order: 'DESC'
      });
      
      if (result.success) {
        dispatch({ type: ActionTypes.SET_LEAVE_REQUESTS, payload: result.data });
      } else {
        throw new Error(result.error);
      }
      
      return result;
      
    } catch (error) {
      setError('leaveRequests', error?.message || 'Unknown error');
      return { success: false, error: error?.message || 'Unknown error' };
    } finally {
      setLoading('leaveRequests', false);
    }
  }, []);

  const createLeaveRequest = useCallback(async (leaveData) => {
    try {
      const result = await databaseService.insert('leave_requests', {
        ...leaveData,
        user_id: state.currentUser?.id,
      });
      
      if (result.success) {
        dispatch({ type: ActionTypes.ADD_LEAVE_REQUEST, payload: result.data });
      }
      
      return result;
      
    } catch (error) {
      return { success: false, error: error?.message || 'Unknown error' };
    }
  }, [state.currentUser]);

  const updateLeaveRequest = useCallback(async (requestId, leaveData) => {
    try {
      const result = await databaseService.update('leave_requests', requestId, leaveData);
      
      if (result.success) {
        dispatch({ type: ActionTypes.UPDATE_LEAVE_REQUEST, payload: result.data });
      }
      
      return result;
      
    } catch (error) {
      return { success: false, error: error?.message || 'Unknown error' };
    }
  }, []);

  // Expenses
  const loadExpenses = useCallback(async (userId) => {
    try {
      setLoading('expenses', true);
      clearError('expenses');
      
      const result = await databaseService.findAll('expenses', {
        where: { user_id: userId },
        orderBy: 'date',
        order: 'DESC'
      });
      
      if (result.success) {
        dispatch({ type: ActionTypes.SET_EXPENSES, payload: result.data });
      } else {
        throw new Error(result.error);
      }
      
      return result;
      
    } catch (error) {
      setError('expenses', error?.message || 'Unknown error');
      return { success: false, error: error?.message || 'Unknown error' };
    } finally {
      setLoading('expenses', false);
    }
  }, []);

  const createExpense = useCallback(async (expenseData) => {
    try {
      const result = await databaseService.insert('expenses', {
        ...expenseData,
        user_id: state.currentUser?.id,
      });
      
      if (result.success) {
        dispatch({ type: ActionTypes.ADD_EXPENSE, payload: result.data });
      }
      
      return result;
      
    } catch (error) {
      return { success: false, error: error?.message || 'Unknown error' };
    }
  }, [state.currentUser]);

  const updateExpense = useCallback(async (expenseId, expenseData) => {
    try {
      const result = await databaseService.update('expenses', expenseId, expenseData);
      
      if (result.success) {
        dispatch({ type: ActionTypes.UPDATE_EXPENSE, payload: result.data });
      }
      
      return result;
      
    } catch (error) {
      return { success: false, error: error?.message || 'Unknown error' };
    }
  }, []);

  const deleteExpense = useCallback(async (expenseId) => {
    try {
      const result = await databaseService.delete('expenses', expenseId);
      
      if (result.success) {
        dispatch({ type: ActionTypes.DELETE_EXPENSE, payload: expenseId });
      }
      
      return result;
      
    } catch (error) {
      return { success: false, error: error?.message || 'Unknown error' };
    }
  }, []);

  // Reference data
  const loadCompanies = useCallback(async () => {
    try {
      const result = await databaseService.findAll('companies');
      if (result.success) {
        dispatch({ type: ActionTypes.SET_COMPANIES, payload: result.data });
      }
      return result;
    } catch (error) {
      return { success: false, error: error?.message || 'Unknown error' };
    }
  }, []);

  const loadRoles = useCallback(async (companyId = null) => {
    try {
      const where = companyId ? { company_id: companyId } : {};
      const result = await databaseService.findAll('roles', { where });
      if (result.success) {
        dispatch({ type: ActionTypes.SET_ROLES, payload: result.data });
      }
      return result;
    } catch (error) {
      return { success: false, error: error?.message || 'Unknown error' };
    }
  }, []);

  // Refresh all data
  const refreshAllData = useCallback(async () => {
    if (!state.currentUser) return;
    
    const userId = state.currentUser.id;
    
    await Promise.all([
      loadDashboardStats(userId),
      loadProjects(userId),
      loadTasks(userId),
      loadTimeEntries(userId),
      loadNotifications(userId),
      loadLeaveRequests(userId),
      loadExpenses(userId),
    ]);
  }, [state.currentUser, loadDashboardStats, loadProjects, loadTasks, loadTimeEntries, loadNotifications, loadLeaveRequests, loadExpenses]);

  // Sync operations
  const performSync = useCallback(async () => {
    try {
      const result = await syncService.performFullSync();
      return result;
    } catch (error) {
      return { success: false, error: error?.message || 'Unknown error' };
    }
  }, []);

  const getSyncStatus = useCallback(() => {
    return syncService.getSyncStatus();
  }, []);

  // Context value
  const value = {
    // State
    ...state,
    
    // User management
    setCurrentUser,
    updateUserProfile,
    
    // Dashboard
    loadDashboardStats,
    
    // Projects
    loadProjects,
    createProject,
    updateProject,
    deleteProject,
    
    // Tasks
    loadTasks,
    createTask,
    updateTask,
    updateTaskProgress,
    deleteTask,
    
    // Time tracking
    loadTimeEntries,
    createTimeEntry,
    updateTimeEntry,
    deleteTimeEntry,
    
    // Notifications
    loadNotifications,
    createNotification,
    markNotificationAsRead,
    
    // Leave requests
    loadLeaveRequests,
    createLeaveRequest,
    updateLeaveRequest,
    
    // Expenses
    loadExpenses,
    createExpense,
    updateExpense,
    deleteExpense,
    
    // Reference data
    loadCompanies,
    loadRoles,
    
    // Utilities
    refreshAllData,
    performSync,
    getSyncStatus,
    setLoading,
    setError,
    clearError,
  };

  return (
    <DatabaseContext.Provider value={value}>
      {children}
    </DatabaseContext.Provider>
  );
}

// Custom hook to use database context
export function useDatabase() {
  const context = useContext(DatabaseContext);
  if (!context) {
    throw new Error('useDatabase must be used within a DatabaseProvider');
  }
  return context;
}

export default DatabaseContext;